/**
 * Wishlist State Management (Valtio)
 * Ported from breitling-storefront-next states/wishlistState.tsx
 *
 * Global state for wishlist highlighting and count.
 * The actual wishlist items are stored in localStorage per country.
 * This state provides reactive UI updates when wishlist changes.
 *
 * @example
 * ```tsx
 * import { wishlistState, wishlistActions } from '@/states/wishlistState';
 * import { useSnapshot } from 'valtio';
 *
 * // In a component
 * const { isHighlighted, itemCount } = useSnapshot(wishlistState);
 *
 * // After wishlist changes
 * wishlistActions.updateFromStorage('US');
 * ```
 */

import { proxy } from 'valtio';
import { clearAllLocalWishlists, getLocalWishlistCount } from '@/lib/wishlist/storage';

// =============================================================================
// Types
// =============================================================================

export interface WishlistState {
  /** Whether wishlist has items (for header icon highlighting) */
  isHighlighted: boolean;
  /** Current item count for badge display */
  itemCount: number;
  /** Current country code context */
  countryCode: string | null;
}

// =============================================================================
// Initial State
// =============================================================================

const initialWishlistState: WishlistState = {
  isHighlighted: false,
  itemCount: 0,
  countryCode: null,
};

// =============================================================================
// Proxy State
// =============================================================================

export const wishlistState = proxy<WishlistState>({ ...initialWishlistState });

// =============================================================================
// Actions
// =============================================================================

export const wishlistActions = {
  /**
   * Update state from localStorage for a country
   * Call this after any wishlist modification
   */
  updateFromStorage: (countryCode: string) => {
    const count = getLocalWishlistCount(countryCode);
    wishlistState.isHighlighted = count > 0;
    wishlistState.itemCount = count;
    wishlistState.countryCode = countryCode;
  },

  /**
   * Set the country context for the wishlist
   */
  setCountry: (countryCode: string) => {
    wishlistState.countryCode = countryCode;
    wishlistActions.updateFromStorage(countryCode);
  },

  /**
   * Increment item count (optimistic update before storage write)
   */
  incrementCount: () => {
    wishlistState.itemCount += 1;
    wishlistState.isHighlighted = true;
  },

  /**
   * Decrement item count (optimistic update before storage write)
   */
  decrementCount: () => {
    wishlistState.itemCount = Math.max(0, wishlistState.itemCount - 1);
    wishlistState.isHighlighted = wishlistState.itemCount > 0;
  },

  /**
   * Clear all wishlist state (valtio state only)
   * Use clearAll() to also clear localStorage and cookies
   */
  clear: () => {
    Object.assign(wishlistState, initialWishlistState);
  },

  /**
   * Clear all wishlist state, localStorage, and cookies
   * Call this on logout to fully clear the wishlist
   */
  clearAll: () => {
    // Dispatch event FIRST to notify WishlistProvider
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('wishlist-cleared'));
    }
    // Clear localStorage and cookies
    clearAllLocalWishlists();
    // Clear valtio state
    Object.assign(wishlistState, initialWishlistState);
  },
};
